/**
 * 🐋 Station Landing Page - Cetalingua FM
 * 
 * Landing page for marine mammal watching companies to become station operators
 * 
 * Key points:
 * - Pre-recorded uploads (NOT live broadcast)
 * - Simple: upload recordings, we handle the rest
 * - Species identification (bottlenose, humpback, manatee + expanding)
 * - Engagement visualizations coming soon
 * - Use existing hydrophones or affordable AudioMoth/HydroMoth
 * - Full tutorials and support
 * 
 * Pricing (Stage 3, 29 Sep 2026): $24.99/month, no setup fee (plans.json)
 */

import React from 'react';
import './StationLanding.css';
import Showcase from '../common/Showcase';

const SHOTS = [
  { file: 'station-app-player.png', kind: 'phone', caption: 'Your station in the app: listeners play your recordings' },
  { file: 'station-app-visualizer.png', kind: 'phone', caption: 'The visualizer shows the calls as they play' },
  { file: 'station-portal-upload.png', kind: 'desktop', caption: 'Upload page: public or private, site details' },
  { file: 'station-portal-results.png', kind: 'desktop', caption: 'Results for every broadcast' },
];

const StationLanding = ({ onSelectPlan, onNavigate }) => {

  return (
    <div className="station-landing-container">
      {/* Hero Section */}
      <div className="station-hero">
        <div className="hero-content">
          <span className="hero-logo">📡</span>
          <h1 className="hero-title">Share Your Ocean Sounds with the World</h1>
          <p className="hero-subtitle">
            Upload your acoustic recordings to Cetalingua FM and connect with whale & dolphin enthusiasts globally. 
            Keep your patrons engaged year-round and attract new customers who love marine life.
          </p>
          <div className="hero-highlight">
            <span className="highlight-icon">✨</span>
            <span className="highlight-text">
              No live streaming required – just upload recordings and we handle everything else!
            </span>
          </div>
          <button className="hero-cta" onClick={() => onSelectPlan('station')}>
            Become a Station Host →
          </button>
        </div>
      </div>

      {/* How It Works - Simple */}
      <div className="simple-section">
        <h2>It's Simpler Than You Think</h2>
        <p className="section-intro">
          No complex streaming setup. No technical headaches. Just upload your recordings and let us do the rest.
        </p>
        <div className="simple-steps">
          <div className="simple-step">
            <div className="step-icon">🎙️</div>
            <h3>1. Record</h3>
            <p>Use your existing hydrophone or an affordable AudioMoth/HydroMoth recorder</p>
          </div>
          <div className="simple-arrow">→</div>
          <div className="simple-step">
            <div className="step-icon">📤</div>
            <h3>2. Upload</h3>
            <p>Upload audio files through our simple station portal</p>
          </div>
          <div className="simple-arrow">→</div>
          <div className="simple-step">
            <div className="step-icon">🐋</div>
            <h3>3. We Handle It</h3>
            <p>We process, identify species, and stream to listeners worldwide</p>
          </div>
        </div>
        <div className="simple-note">
          <span className="note-icon">💡</span>
          <p>
            <strong>Pre-recorded, not live.</strong> Upload batches of recordings whenever convenient – 
            daily, weekly, or seasonally. Your station plays continuously while you focus on your business.
          </p>
        </div>
      </div>

      <Showcase intro="What listeners see in the app – and where you manage your station." items={SHOTS} />

      {/* Why Become a Station */}
      <div className="benefits-section">
        <h2>Why Your Whale Watching Company Needs a Station</h2>
        <div className="benefits-grid">
          <div className="benefit-card featured">
            <div className="benefit-icon">🔄</div>
            <h3>Keep Patrons Engaged Year-Round</h3>
            <p>
              Don't lose touch with customers after their trip! Your station gives them a way to stay 
              connected to the ocean – and to your company – even when they're back home.
            </p>
          </div>
          <div className="benefit-card featured">
            <div className="benefit-icon">❄️</div>
            <h3>Reach Them in Off-Season</h3>
            <p>
              When tours aren't running, your station keeps your brand in front of customers. They'll 
              think of you first when planning their next whale watching adventure.
            </p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">🌍</div>
            <h3>Attract New Customers</h3>
            <p>
              Thousands of app users discover stations from around the world. Your recordings introduce 
              your company to passionate marine life lovers who may book a future trip.
            </p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">💝</div>
            <h3>Create Loyal, Returning Customers</h3>
            <p>
              When someone listens to "their" whales from your location, they form an emotional connection. 
              That loyalty translates to repeat bookings and word-of-mouth referrals.
            </p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">⭐</div>
            <h3>Stand Out from Competitors</h3>
            <p>
              Most whale watching companies don't offer this. Having a station on Cetalingua FM sets you 
              apart as an innovative, conservation-minded operation that goes the extra mile.
            </p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">🎯</div>
            <h3>Direct Access to Enthusiasts</h3>
            <p>
              Reach people who selected interest in the exact species you feature. These aren't casual 
              listeners – they're your ideal customers.
            </p>
          </div>
        </div>
      </div>

      {/* Species Identification */}
      <div className="identification-section">
        <div className="id-content">
          <div className="id-text">
            <h2>🔬 Automatic Species Identification</h2>
            <p className="id-intro">
              When you upload recordings, our AI analyzes them and identifies calls present. 
              Listeners see which species are vocalizing – making your station more engaging and educational.
            </p>
            <div className="species-available">
              <h3>Currently Identifying:</h3>
              <div className="species-tags">
                <span className="species-tag">🐬 Bottlenose Dolphins</span>
                <span className="species-tag">🐋 Humpback Whales</span>
                <span className="species-tag">🦭 Manatees</span>
              </div>
              <p className="expanding-note">
                <span className="expanding-icon">🚀</span>
                <strong>Expanding:</strong> sperm whale clicks are already analysed in the research portal;
                orcas, gray whales and more species are being added to our identification system.
              </p>
            </div>
          </div>
          <div className="id-visual">
            <div className="visual-card">
              <div className="visual-header">
                <span className="visual-icon">📊</span>
                <span>In the app</span>
              </div>
              <h3>Interactive Visualizations</h3>
              <p>
                Listeners see the calls while your recording plays: the app's visualizer shows the
                sounds, and the detections tell them which animals were heard.
              </p>
              <ul className="visual-features">
                <li>✓ Spectrogram displays</li>
                <li>✓ Call type identification</li>
                <li>✓ Species activity indicators</li>
                <li>✓ Educational overlays</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Equipment Section */}
      <div className="equipment-section">
        <h2>Easy Equipment Options</h2>
        <p className="section-intro">
          Use what you have, or get started affordably. No expensive equipment required.
        </p>
        <div className="equipment-grid">
          <div className="equipment-card">
            <div className="equipment-icon">🎤</div>
            <h3>Use Your Existing Hydrophone</h3>
            <p>
              Already have a hydrophone? Great! Any recording equipment that captures underwater audio 
              will work. Just export your files and upload.
            </p>
            <ul>
              <li>✓ Works with any hydrophone brand</li>
              <li>✓ Standard audio formats accepted</li>
              <li>✓ No special hardware needed</li>
            </ul>
          </div>
          <div className="equipment-card recommended">
            <div className="recommended-badge">AFFORDABLE OPTION</div>
            <div className="equipment-icon">🔊</div>
            <h3>AudioMoth / HydroMoth</h3>
            <p>
              Don't have a hydrophone? The AudioMoth and HydroMoth are affordable, easy-to-use 
              recorders perfect for marine acoustic monitoring.
            </p>
            <ul>
              <li>✓ Under $100 per unit</li>
              <li>✓ Long battery life</li>
              <li>✓ Scheduled recording</li>
              <li>✓ Waterproof housing available</li>
            </ul>
            <div className="equipment-note">
              We provide full video and PDF tutorials for AudioMoth/HydroMoth setup in your station portal!
            </div>
          </div>
        </div>
      </div>

      {/* Support Section */}
      <div className="support-section">
        <div className="support-card">
          <div className="support-icon">🤝</div>
          <h2>We're Here to Help</h2>
          <p>
            Setting up a station shouldn't be complicated. We provide comprehensive support to get you 
            recording and uploading quickly.
          </p>
          <div className="support-features">
            <div className="support-feature">
              <span className="feature-icon">📹</span>
              <div>
                <h4>Video Tutorials</h4>
                <p>Step-by-step guides for equipment setup, recording best practices, and file uploads</p>
              </div>
            </div>
            <div className="support-feature">
              <span className="feature-icon">📄</span>
              <div>
                <h4>PDF Guides</h4>
                <p>Downloadable documentation for AudioMoth/HydroMoth configuration and deployment</p>
              </div>
            </div>
            <div className="support-feature">
              <span className="feature-icon">💬</span>
              <div>
                <h4>Direct Support</h4>
                <p>Have questions? Our team is available to answer questions about setup, recordings, and uploads</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Station Portal Preview */}
      <div className="portal-section">
        <h2>Your Station Dashboard</h2>
        <p className="section-intro">
          Manage everything from one simple portal
        </p>
        <div className="portal-features">
          <div className="portal-feature">
            <span className="portal-icon">📤</span>
            <h3>Easy Upload</h3>
            <p>Drag & drop audio files or batch upload recordings</p>
          </div>
          <div className="portal-feature">
            <span className="portal-icon">📊</span>
            <h3>Listener Stats</h3>
            <p>See how many people are tuning in to your station</p>
          </div>
          <div className="portal-feature">
            <span className="portal-icon">🐋</span>
            <h3>Species Reports</h3>
            <p>View which species were detected in your recordings</p>
          </div>
          <div className="portal-feature">
            <span className="portal-icon">📚</span>
            <h3>Tutorials</h3>
            <p>Access video and PDF guides for equipment setup</p>
          </div>
          <div className="portal-feature">
            <span className="portal-icon">⚙️</span>
            <h3>Station Settings</h3>
            <p>Customize your station name, location, and description</p>
          </div>
          <div className="portal-feature">
            <span className="portal-icon">🗺️</span>
            <h3>Map Presence</h3>
            <p>Your station appears on the global interactive map</p>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="pricing-section">
        <h2>Simple Pricing</h2>
        <div className="pricing-card-wrapper">
          <div className="station-pricing-card">
            <div className="pricing-header">
              <span className="pricing-icon">📡</span>
              <h3>Station Host</h3>
            </div>
            <div className="pricing-price">
              <span className="price-amount">$24.99</span>
              <span className="price-period">/month</span>
            </div>
            <div className="pricing-features">
              <h4>What's Included:</h4>
              <ul>
                <li>✓ 3 GB of uploads per month (more can be added)</li>
                <li>✓ Automatic species identification</li>
                <li>✓ Station on global map</li>
                <li>✓ Listener statistics</li>
                <li>✓ Full tutorial library</li>
                <li>✓ Technical support</li>
                <li>✓ Free Cetalingua FM app account (listener)</li>
              </ul>
              <h4 style={{ marginTop: '16px' }}>Getting started (included):</h4>
              <ul className="setup-list">
                <li>• Station configuration & testing</li>
                <li>• Onboarding support</li>
                <li>• Equipment consultation</li>
              </ul>
            </div>
            <button 
              className="pricing-button"
              onClick={() => onSelectPlan('station')}
            >
              Become a Station Host →
            </button>
          </div>
        </div>
      </div>

      {/* Also Advertise Section */}
      <div className="advertise-upsell">
        <div className="upsell-card">
          <div className="upsell-badge">SAVE $9.99/MONTH</div>
          <div className="upsell-icon">📢</div>
          <div className="upsell-content">
            <h3>Also Want to Advertise Your Tours?</h3>
            <p>
              Combine station hosting with advertising to promote your whale watching tours directly 
              to Cetalingua FM users. Bundle and save!
            </p>
            <div className="upsell-comparison">
              <div className="comparison-item">
                <span className="comparison-label">Station alone:</span>
                <span className="comparison-price">$24.99/mo</span>
              </div>
              <div className="comparison-item">
                <span className="comparison-label">Advertiser alone:</span>
                <span className="comparison-price">$39.99/mo</span>
              </div>
              <div className="comparison-item combined">
                <span className="comparison-label">Both together:</span>
                <span className="comparison-price">$54.99/mo</span>
                <span className="comparison-savings">Save $9.99/mo!</span>
              </div>
            </div>
            <button 
              className="upsell-button"
              onClick={() => onSelectPlan('combined')}
            >
              🐋 Get Combined Package →
            </button>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h4>Is this live streaming?</h4>
            <p>No! This is pre-recorded. You upload audio files whenever convenient, and we stream them to listeners. No live streaming equipment or constant internet required.</p>
          </div>
          <div className="faq-item">
            <h4>How often do I need to upload?</h4>
            <p>That's up to you! Some stations upload weekly, others monthly or seasonally. We recommend regular uploads to keep content fresh, but there's no strict requirement.</p>
          </div>
          <div className="faq-item">
            <h4>What audio format do you accept?</h4>
            <p>We accept WAV, MP3, FLAC, and other common formats. Our portal handles the conversion and processing automatically.</p>
          </div>
          <div className="faq-item">
            <h4>Do I need expensive equipment?</h4>
            <p>No! You can use any existing hydrophone, or get started with an affordable AudioMoth/HydroMoth for under $100. We provide full setup tutorials.</p>
          </div>
          <div className="faq-item">
            <h4>Can I also advertise my tours?</h4>
            <p>Yes! Get the combined package for $54.99/mo and save $9.99/mo compared to separate subscriptions. Reach listeners directly with your tour promotions.</p>
          </div>
          <div className="faq-item">
            <h4>What species can you identify?</h4>
            <p>Currently: bottlenose dolphins, humpback whales, and manatees. We're actively expanding to include orcas, gray whales, sperm whales, and more.</p>
          </div>
          <div className="faq-item">
            <h4>Do I get support?</h4>
            <p>Absolutely! Your station portal includes video and PDF tutorials, plus our team is available to answer questions about equipment, recording, and uploads.</p>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="cta-section">
        <h2>Ready to Share Your Ocean Sounds?</h2>
        <p>Join Cetalingua FM and connect with marine life enthusiasts worldwide</p>
        <div className="cta-buttons">
          <button 
            className="cta-button primary"
            onClick={() => onSelectPlan('station')}
          >
            📡 Become a Station Host - $24.99/mo
          </button>
          <button 
            className="cta-button secondary"
            onClick={() => onSelectPlan('combined')}
          >
            🐋 Get Combined Package - $54.99/mo
          </button>
        </div>
        <p className="cta-note">
          Questions about equipment or setup? <a href="mailto:stations@cetalingua.fm">stations@cetalingua.fm</a>
        </p>
      </div>

      {/* Footer */}
      <div className="station-footer">
        <div className="footer-links">
          <a href="#" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('terms'); }}>Terms of Service</a>
          <span className="footer-divider">•</span>
          <a href="#" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('privacy'); }}>Privacy Policy</a>
          <span className="footer-divider">•</span>
          <a href="#" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('contact'); }}>Contact Us</a>
        </div>
        <p className="footer-copyright">© 2025 Cetalingua FM. All rights reserved.</p>
        <p className="existing-partner">
          Already a partner? <a href="#" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('login'); }}>Sign in here</a>
        </p>
      </div>
    </div>
  );
};

export default StationLanding;
